import { FC } from 'react';
import {
  Box,
  Select,
  MenuItem,
  Typography,
  Button,
  SelectChangeEvent,
} from '@mui/material';
import { PaginationControlProps } from 'src/models/Pagination.interface';
import { ExpandMoreOutlined } from '@mui/icons-material';

const PaginationControl: FC<PaginationControlProps> = ({
  totalItems,
  page,
  setPage,
  pageSize,
  setPageSize,
  loading,
}) => {
  const totalPages = Math.ceil(totalItems / pageSize);

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mt={2}
    >
      <Box display="flex" alignItems="center">
        <Typography
          sx={{
            fontFamily: 'Outfit',
            fontSize: '12px',
            fontWeight: 400,
            marginRight: '8px',
            color: 'var(--instructions)',
          }}
        >
          Items per page:
        </Typography>

        <Box
          position="relative"
          sx={{ display: 'inline-block', width: '80px' }}
        >
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            disabled={loading}
            IconComponent={ExpandMoreOutlined}
            sx={{
              width: '70px',
              height: '25px',
              padding: '1px',
              fontSize: '12px',
              fontWeight: 400,
              fontFamily: 'Outfit',
              color: 'var(--primary-navy)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--border) !important',
              },
            }}
          >
            {[15, 25, 50, 100].map((size) => (
              <MenuItem
                key={size}
                value={size}
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Outfit',
                  fontWeight: '400',
                  color: 'var(--primary-navy)',
                  '&:hover': {
                    backgroundColor: 'var(--inactive-btn)',
                    color: 'var(--primary-blue)',
                  },
                }}
              >
                {size}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* Page Selector + Navigation */}
      <Box display="flex" alignItems="center">
        <Typography
          sx={{
            fontFamily: 'Outfit',
            fontSize: '12px',
            fontWeight: 400,
            color: 'var(--instructions)',
            marginRight: '10px',
          }}
        >
          Page:
        </Typography>

        <Select
          value={page}
          onChange={(e) => setPage(Number(e.target.value))}
          disabled={loading}
          IconComponent={ExpandMoreOutlined}
          sx={{
            width: '60px',
            height: '25px',
            padding: '1px',
            fontSize: '12px',
            fontWeight: 400,
            fontFamily: 'Outfit',
            color: 'var(--primary-navy)',
            marginRight: '8px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--border) !important',
            },
          }}
        >
          {Array.from({ length: totalPages }, (_, i) => (
            <MenuItem
              key={i + 1}
              value={i + 1}
              sx={{
                fontSize: '12px',
                fontFamily: 'Outfit',
                fontWeight: '400',
                color: 'var(--primary-navy)',
                '&:hover': {
                  backgroundColor: 'var(--inactive-btn)',
                  color: 'var(--primary-blue)',
                },
              }}
            >
              {i + 1}
            </MenuItem>
          ))}
        </Select>

        <Typography
          sx={{
            fontFamily: 'Outfit',
            fontSize: '12px',
            fontWeight: 400,
            color: 'var(--instructions)',
            marginRight: '10px',
          }}
        >
          of {totalPages}
        </Typography>

        <Button
          disabled={page === 1 || loading}
          onClick={() => setPage(page - 1)}
          sx={{
            border: '1px solid',
            borderColor: 'var(--border-blue)',
            mr: 1,
            minWidth: '30px',
            color: 'var(--bg-color)',
            backgroundColor: 'var(--primary-blue)',
            '&:disabled': {
              color: 'var(--primary-blue)',
              backgroundColor: 'var(--inactive-btn)',
              cursor: 'not-allowed',
            },
            '&:hover': {
              backgroundColor: '#154083',
            },
          }}
        >
          <i className="bx bx-chevron-left"></i>
        </Button>

        <Button
          disabled={page === totalPages || loading}
          onClick={() => setPage(page + 1)}
          sx={{
            border: '1px solid',
            borderColor: 'var(--border-blue)',
            minWidth: '30px',
            color: 'var(--bg-color)',
            backgroundColor: 'var(--primary-blue)',
            '&:disabled': {
              color: 'var(--primary-blue)',
              backgroundColor: 'var(--inactive-btn)',
              cursor: 'not-allowed',
            },
            '&:hover': {
              backgroundColor: '#154083',
            },
          }}
        >
          <i className="bx bx-chevron-right"></i>
        </Button>
      </Box>
    </Box>
  );
};

export default PaginationControl;
